<template>
  <InteractiveModal
    ref="interactiveModalRef"
    modalId="jbiCriteriaAcknowledgmentModal"
    :modalTitle="'Acknowledge JBI Criteria Update?'"
    :modalContent="jbiCriteriaUpdatesText"
    :isModalActive="isModalActive"
    :isMinimized="isMinimized"
    :draggable="true"
    @close="$emit('close')"
    @minimize="$emit('minimize')"
    @restore="$emit('restore')"
  >
    <template slot="modal-body">
      <div class="columns">
        <div
          :class="[
            'column',
            'bg-primary-light',
            $style.updatedJbiCriteriaNotification,
          ]"
        >
          <span>
            All updated criteria will only be available for newly created data
            collection period.
          </span>
        </div>
      </div>
      <div class="columns mb1">
        <div :class="['column', $style.initialText]">
          <span>{{ `Here are the following JBI criteria` }}</span>
        </div>
      </div>
      <div
        v-for="(jbiCriteria, index) in JBICriteriaUpdates"
        :key="`${jbiCriteria.status}_${index}`"
        class="mb1 mt1"
      >
        <template v-if="jbiCriteria.criteriaList.length > 0">
          <h6 :class="$style.jbiCriteriaStatusTitle">
            {{ jbiCriteria.status }}
          </h6>
          <li
            :class="$style.criteriaList"
            v-for="(criteria, index) in jbiCriteria.criteriaList"
            :key="`criteria_${index}`"
          >
            <div class="columns">
              <div
                v-if="jbiCriteria.status !== jbiCriteriaStatus.NEW"
                :class="['column is-half']"
              >
                <div :class="$style.criteriaContainer">
                  <div :class="$style.criteriaHeader">
                    Your current criteria
                  </div>
                  <div :class="$style.criteriaDocumentId">
                    {{
                      criteria.currentCriteria &&
                      criteria.currentCriteria.documentId
                    }}
                  </div>
                  <b-tooltip
                    type="is-dark"
                    :active="
                      criteria.currentCriteria &&
                      criteria.currentCriteria.title &&
                      criteria.currentCriteria.title.length > 70
                    "
                    :label="
                      criteria.currentCriteria && criteria.currentCriteria.title
                    "
                    multilined
                  >
                    <div :class="$style.criteriaTitle">
                      {{
                        criteria.currentCriteria &&
                        criteria.currentCriteria.title
                      }}
                    </div>
                  </b-tooltip>
                  <div
                    :class="$style.criteriaOptions"
                    v-if="
                      criteria.currentCriteria &&
                      criteria.currentCriteria.criteriaOptions
                    "
                  >
                    <template
                      v-if="
                        criteria.currentCriteria &&
                        criteria.currentCriteria.criteriaType ===
                          criteriaType.BOOLEAN
                      "
                    >
                      <div
                        v-for="(
                          booleanOption, index
                        ) in criteria.currentCriteria &&
                        criteria.currentCriteria.criteriaOptions"
                        :key="`${index}${booleanOption}`"
                      >
                        <b-radio
                          disabled
                          native-value="booleanOption"
                          size="is-small"
                        >
                          <span :class="$style.optionText">
                            {{ booleanOption }}
                          </span>
                        </b-radio>
                      </div>
                    </template>

                    <template
                      v-if="
                        criteria.currentCriteria &&
                        criteria.currentCriteria.criteriaType ===
                          criteriaType.RANGE
                      "
                    >
                      <div>
                        Min:
                        {{
                          criteria.currentCriteria &&
                          criteria.currentCriteria.criteriaOptions &&
                          criteria.currentCriteria.criteriaOptions[0]
                        }}
                      </div>
                      <div>
                        Max:
                        {{
                          criteria.currentCriteria &&
                          criteria.currentCriteria.criteriaOptions &&
                          criteria.currentCriteria.criteriaOptions[1]
                        }}
                      </div>
                    </template>

                    <template
                      v-if="
                        criteria.currentCriteria &&
                        criteria.currentCriteria.criteriaType ===
                          criteriaType.CHECKBOXES
                      "
                    >
                      <div
                        v-for="(
                          checkboxesOption, index
                        ) in criteria.currentCriteria &&
                        criteria.currentCriteria.criteriaOptions"
                        :key="`${index}${checkboxesOption}`"
                      >
                        <b-checkbox disabled type="is-info">
                          <span :class="$style.optionText">
                            {{ checkboxesOption }}
                          </span>
                        </b-checkbox>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div
                v-if="criteria.status !== jbiCriteriaStatus.NEW"
                class="column flex verical-center no-padding"
              >
                <span :class="['icon', $style.iconRight]">
                  <i class="fas fa-chevron-right"></i>
                </span>
              </div>
              <div
                :class="{
                  ['column']: true,
                  ['is-half']: jbiCriteria.status !== jbiCriteriaStatus.NEW,
                }"
              >
                <div
                  v-if="criteria.status === jbiCriteriaStatus.DELETED"
                  :class="$style.deletedCriteriaContainer"
                >
                  <div :class="$style.deleteCriteriaDetails">
                    <div
                      :class="[$style.criteriaHeader, $style.paddingHorizontal]"
                    >
                      Deleted JBI Criteria
                    </div>
                    <div
                      :class="[
                        $style.criteriaDocumentId,
                        $style.paddingHorizontal,
                      ]"
                    >
                      {{ criteria.connectPlusDocumentId }}
                    </div>
                  </div>
                  <div :class="$style.deleteCriteriaBox">
                    <p :class="$style.deleteCriteriaText">
                      This criteria has been deleted
                    </p>
                  </div>
                </div>

                <div v-else :class="$style.criteriaContainer">
                  <div :class="$style.criteriaHeader">
                    {{
                      criteria.status === jbiCriteriaStatus.NEW
                        ? 'New JBI criteria'
                        : 'Updated JBI criteria'
                    }}
                    <b-tag
                      v-if="criteria.status === jbiCriteriaStatus.NEW"
                      type="is-primary"
                    >
                      <p class="is-text-info">NEW</p>
                    </b-tag>
                  </div>
                  <div :class="$style.criteriaDocumentId">
                    {{ criteria.connectPlusDocumentId }}
                  </div>
                  <b-tooltip
                    type="is-dark"
                    :active="
                      criteria.content &&
                      criteria.content.title &&
                      criteria.content.title.length > 70
                    "
                    :label="criteria.content && criteria.content.title"
                    multilined
                  >
                    <div :class="$style.criteriaTitle">
                      {{ criteria.content && criteria.content.title }}
                    </div>
                  </b-tooltip>

                  <div :class="$style.criteriaOptions">
                    <template
                      v-if="
                        criteria.content &&
                        criteria.content.type === criteriaType.BOOLEAN &&
                        'booleanOptions' in criteria.content
                      "
                    >
                      <div
                        v-for="(booleanOption, index) in criteria.content
                          .booleanOptions"
                        :key="`${index}${booleanOption}`"
                      >
                        <b-radio
                          disabled
                          native-value="booleanOption"
                          size="is-small"
                        >
                          <span :class="$style.optionText">
                            {{ booleanOption }}
                          </span>
                        </b-radio>
                      </div>
                    </template>

                    <template
                      v-if="
                        criteria.content &&
                        criteria.content.type === criteriaType.RANGE &&
                        'range' in criteria.content
                      "
                    >
                      <div>
                        Min:
                        {{ criteria.content.range[0] }}
                      </div>
                      <div>
                        Max:
                        {{ criteria.content.range[1] }}
                      </div>
                    </template>

                    <template
                      v-if="
                        criteria.content &&
                        criteria.content.type === criteriaType.CHECKBOXES &&
                        'checkboxesOptions' in criteria.content
                      "
                    >
                      <div
                        v-for="(checkboxesOption, index) in criteria.content
                          .checkboxesOptions"
                        :key="`${index}${checkboxesOption}`"
                      >
                        <b-checkbox disabled type="is-info">
                          <span :class="$style.optionText">
                            {{ checkboxesOption }}
                          </span>
                        </b-checkbox>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </template>
      </div>

      <div class="columns">
        <div :class="['column', $style.initialText]">
          <span>
            I hereby acknowledge that I have carefully considered the following
            criteria.
          </span>
        </div>
      </div>
    </template>

    <template slot="modal-footer">
      <a :class="$style.cancelBtn" @click="handleClose"> Cancel </a>
      <button
        class="button is-primary is-rounded"
        @click="acknowledgeJBICriteriaChanges"
      >
        Acknowledge
      </button>
    </template>
  </InteractiveModal>
</template>
<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, namespace, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';
import InteractiveModal from '@/components/base/InteractiveModal.vue';
import {
  JBICriteriaStatus,
  ProjectCriteriaAcknowledgement,
  ProjectDetailPayload,
  UpdatedJBICriteriaResponse,
  UpdateJBICriteriaAcknowledgmentPayload,
} from '../../../store/modules/projects/types/projects.types';
import { ApiState } from '../../../store/types/general.types';
import { RootState } from '../../../store/store';
import { AxiosError } from 'axios';
import { CRITERION_TYPE } from '../../../store/types/criterions.types';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    InteractiveModal,
  },
})
export default class JBICriteriaAcknowledgmentModal extends Vue {
  @Prop() public projectId!: number;
  @Prop() public updatedJBICriteriaResponse!: UpdatedJBICriteriaResponse;
  @Prop()
  public projectCriteriaAcknowledgement!: ProjectCriteriaAcknowledgement;

  @Prop({ type: Boolean, required: true })
  public isModalActive!: boolean;

  @Prop({ type: Boolean, required: true })
  public isMinimized!: boolean;

  @Action('projects/updateJBICriteriaAcknowledgment')
  public updateJBICriteriaAcknowledgment!: (
    payload: UpdateJBICriteriaAcknowledgmentPayload,
  ) => void;

  @Action('projects/getUpdatedJBICriteriaWithAcknowledgement')
  public getUpdatedJBICriteriaWithAcknowledgement!: (projectId: number) => void;

  @State(
    (state: RootState) =>
      state.projects.apiState.getUpdateJBICriteriaAcknowledgment,
  )
  public getUpdateJBICriteriaAcknowledgmentState!: ApiState;

  @State((state) => state.projects.projectDetail)
  public projectDetail!: ProjectDetailPayload;

  public async handleClose(): Promise<void> {
    const modal = this.$refs.interactiveModalRef as InteractiveModal;
    if (modal) {
      modal.handleAttemptClose();
    }
  }

  get criteriaType(): typeof CRITERION_TYPE {
    return CRITERION_TYPE;
  }

  get jbiCriteriaStatus(): typeof JBICriteriaStatus {
    return JBICriteriaStatus;
  }

  get JBICriteriaUpdates() {
    return [
      {
        status: JBICriteriaStatus.UPDATED,
        criteriaList: this.updatedJBICriteriaResponse.updatedJBICriteria,
      },
      {
        status: JBICriteriaStatus.NEW,
        criteriaList: this.updatedJBICriteriaResponse.newJBICriteria,
      },
      {
        status: JBICriteriaStatus.DELETED,
        criteriaList: this.updatedJBICriteriaResponse.deletedJBICriteria,
      },
    ];
  }

  public acknowledgeJBICriteriaChanges(): void {
    const payload: UpdateJBICriteriaAcknowledgmentPayload = {
      projectId: this.projectId,
      projectCriteriaAcknowledgement: this.projectCriteriaAcknowledgement,
    };
    this.updateJBICriteriaAcknowledgment(payload);
  }

  @Watch('getUpdateJBICriteriaAcknowledgmentState.success')
  @isTruthy
  public watchGetUpdateJBICriteriaAcknowledgmentStateSuccess(): void {
    Toast.open({
      message: 'Successfully Acknowledged.',
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
    this.$emit('close');
    this.getUpdatedJBICriteriaWithAcknowledgement(this.projectId);
  }

  @Watch('getUpdateJBICriteriaAcknowledgmentState.error')
  @isTruthy
  public watchGetUpdateJBICriteriaAcknowledgmentStateError(
    error: AxiosError,
  ): void {
    Toast.open({
      message:
        error && error.response && error.response.data
          ? error.response.data.message
          : 'Something went wrong. Please try again.',
      position: 'is-top',
      type: 'is-danger',
      duration: 3500,
    });
    this.$emit('close');
  }

  /**
   * Computed property for modal content to prevent Vue prop mutation warnings.
   * Creates an intermediary between static text and InteractiveModal prop.
   */
  get jbiCriteriaUpdatesText(): string {
    return 'JBI Criteria Updates';
  }
}
</script>
<style lang="scss" module>
.updatedJbiCriteriaNotification {
  margin: 12px;
  padding: 0.75rem !important;
  border-radius: 4px;

  div > div {
    color: $texty-gray;
  }

  span {
    font-size: $size-5;
    color: $texty-gray;
  }
}

.jbiCriteriaStatusTitle {
  color: $grey;
  font-size: $size-4;
  font-weight: bold;
}

.initialText {
  font-size: $size-4;
  line-height: 22px;
  color: $texty-gray;
}

.criteriaList {
  display: block;
  padding: 0.75rem 0;
}

.criteriaContainer {
  background: $white;
  border: 1px solid $grey-border-2;
  border-radius: 4px;
  padding: 15px;
  height: 100% !important;
}

.criteriaContainer div {
  padding-top: 8px;
}

.criteriaHeader {
  font-weight: $weight-semibold;
  font-size: $size-5;
  color: $texty-gray;
  text-transform: uppercase;
}

.criteriaDocumentId {
  font-size: $size-5;
  color: $grey-3;
}

.criteriaTitle {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 500;
  font-size: $size-5;
  color: $texty-gray;
}

.criteriaOptions {
  padding-bottom: 5px;
  color: $grey;
}

.optionText {
  display: block;
  font-weight: 500;
  font-size: $size-5;
  color: $grey-3;
  line-height: 17px;
  white-space: nowrap;
}

.deletedCriteriaContainer {
  background-color: $danger-light;
  border: 1px solid $grey-border-2;
  border-radius: 4px;
  height: 100% !important;

  div {
    padding-top: 8px;
  }
}

.deleteCriteriaDetails {
  background-color: $white;
  padding-top: 15px !important;
  padding-bottom: 30px !important;
}
.deleteCriteriaBox {
  margin-top: 20px;
  padding: 10px 15px;
  background: $danger-light;
  border-radius: 0px 0px 4px 4px;
}

.deleteCriteriaText {
  font-weight: 700;
  font-size: $size-5;
  line-height: 18px;
  color: $primary-red;
}

.paddingHorizontal {
  padding-left: 15px !important;
  padding-right: 15px !important;
}

.cancelBtn {
  color: $primary;
  font-size: $size-3;
  padding-right: 20px;
  font-weight: bold;
}

.iconRight {
  color: $texty-gray;
  width: 1px;
  height: 1px;
  font-size: $size-4;
}
</style>
